class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;
    if (typeof this.onFormSubmit === 'function' && this.input.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!newSearchTerm.length) {
      this.searchTerm = '';
      this.clearResults();
      this.showFeatured();
      return;
    }
    const previousSearchTerm = this.searchTerm;
    this.searchTerm = newSearchTerm;
    this.updateSearchForTerm(previousSearchTerm, newSearchTerm);
    this.hideFeatured();
    this.hideEmptyState();
    this.getSearchResults(newSearchTerm);
  }

  clearResults() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = '';
    }
    const dropdown = this.querySelector('.predictive-search');
    if (dropdown) {
      dropdown.style.display = '';
    }
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.isOpen = false;
    if (this.input) {
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-activedescendant', '');
    }
    this.resultsMaxHeight = false;
  }

  /* ── FEATURED (shown when input is empty) ── */
  showFeatured() {
    const featured = this.querySelector('.predictive-search__featured');
    if (!featured) {
      this.showEmptyState();
      return;
    }
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = '';
    }
    this.removeAttribute('results');
    this.removeAttribute('loading');

    const resultContainer = this.querySelector('.predictive-search__results');
    if (resultContainer) resultContainer.style.display = 'none';

    featured.removeAttribute('hidden');
    featured.style.display = 'block';

    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;

    const dropdown = this.querySelector('.predictive-search');
    if (dropdown) {
      dropdown.style.display = 'block';
      dropdown.style.maxHeight = '';
      dropdown.style.overflowY = '';
    }
  }

  hideFeatured() {
    const featured = this.querySelector('.predictive-search__featured');
    if (featured) {
      featured.style.display = 'none';
      featured.setAttribute('hidden', '');
    }
    const resultContainer = this.querySelector('.predictive-search__results');
    if (resultContainer) resultContainer.style.display = '';
  }

  showEmptyState() {
    if (!this.input) return;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    const emptyState = this.querySelector('.predictive-search__empty-state');
    if (emptyState) emptyState.style.display = 'block';
    const dropdown = this.querySelector('.predictive-search');
    if (dropdown) {
      dropdown.style.display = 'block';
      dropdown.style.maxHeight = '';
      dropdown.style.overflowY = '';
    }
  }

  hideEmptyState() {
    const emptyState = this.querySelector('.predictive-search__empty-state');
    if (emptyState) emptyState.style.display = 'none';
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) {
      this.searchTerm = '';
      this.clearResults();
      this.showFeatured();
      return;
    }
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(currentSearchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.searchTerm = '';
      this.clearResults();
      this.showFeatured();
      event.preventDefault();
      return;
    }
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText) return;
    if (previousTerm && currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) return;
    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter(el => el.offsetParent !== null);
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;
    let selectedElementIndex = -1;
    for (let i = 0; i < allVisibleElements.length; i++) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
        break;
      }
    }
    if (this.statusElement) this.statusElement.textContent = '';
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }
    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;
    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', activeElement.id || '');
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }
    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(text => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search section not found.');
        const resultsMarkup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        if (this.getQuery() !== searchTerm) return;
        this.renderSearchResults(resultsMarkup);
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch(error => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        searchDeferred?.reject(error);
        this.close();
        console.error('Predictive search error:', error);
      });
  }

  getTotalResultCount() {
    return (
      parseInt(
        this.predictiveSearchResults
          ?.querySelector('[data-total-results]')?.dataset.totalResults
      ) || 0
    );
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;
    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(new SearchUpdateEvent({
      search: { query },
      promise: deferred.promise,
    }));
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    if (this.statusElement) this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.hideFeatured();
    this.hideEmptyState();
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) this.setLiveRegionText(countElement.textContent);
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    if (!header) return window.innerHeight;
    this.resultsMaxHeight = window.innerHeight - header.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;
    /* No maxHeight — let CSS control height */
    this.predictiveSearchResults.style.maxHeight = '';
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
    const dropdown = this.querySelector('.predictive-search');
    if (dropdown) dropdown.style.display = '';
    this.hideFeatured();
    this.hideEmptyState();
  }
}

customElements.define('predictive-search', PredictiveSearch);
